// client/src/features/notifications/enums.ts — the wire enums and the two REGISTRIES · ALR-004.
//
// Same construction as `features/dashboard/enums.ts` for the enums: the generated client gives
// each one as a string-literal UNION, a union has no runtime value, and an exhaustive
// `Record<T, true>` is what makes `tsc` fail in the PR that regenerates the client if a member
// is added or dropped. Labels live in `romanian.json` under `enums.{EnumName}.{VALUE}`
// (04 §9.6.3) and `__tests__/enum-parity.test.ts` holds them to these lists, because `enums.*`
// is reserved in `reserved-keys.json` and neither half of the i18n gate can see a missing one.
//
// ── The two registries are NOT enums, and that is why they look different ────────────────
// `TaskItem.TaskKind` and `NotificationRule.RuleType` are `varchar` columns over a C# registry
// of `const string`s (03 §15.2, §15.4), deliberately extensible without a migration — MAINT-004
// added `METER_INDEX_REQUEST` and OAT-002 added `PERSONAL_RENT_DUE` that way. So the wire types
// them `string`, the compiler can hold nothing to them, and the label tables below are keyed by
// string with an explicit fallback for the value this client has never heard of.
//
// The labels are CLOSURES over `t` rather than key strings, for `nav-items.ts`'s reason: a
// stored key would be `t(table[kind])`, a computed key the extractor cannot see, and the §9.6.5
// gate fails it. A closure keeps the literal at the call site while the table stays a table.
// `__tests__/enum-parity.test.ts` reads `TaskKinds.cs` and `NotificationRuleCatalog.cs` and
// fails when a registry value has no row here.

import type { TFunction } from 'i18next';

import type { NotificationPriority, TaskDueWindow, TaskItemStatus } from '@/client';
import type { IconName } from '@/components/icons';
import { fiscalVisible } from '@/config/fiscal';

/** 04 §4.8's severity ladder, in the domain's own order — CRITICAL first is also the sort. */
const NOTIFICATION_PRIORITY_ORDER: Record<NotificationPriority, true> = {
  CRITICAL: true,
  IMPORTANT: true,
  INFO: true,
};

export const NOTIFICATION_PRIORITIES = Object.keys(
  NOTIFICATION_PRIORITY_ORDER,
) as readonly NotificationPriority[];

/** 03 §15.4's lifecycle. The order is the domain enum's, which is also the filter's order. */
const TASK_ITEM_STATUS_ORDER: Record<TaskItemStatus, true> = {
  OPEN: true,
  IN_PROGRESS: true,
  DONE: true,
  DISMISSED: true,
};

export const TASK_ITEM_STATUSES = Object.keys(TASK_ITEM_STATUS_ORDER) as readonly TaskItemStatus[];

/**
 * `TaskDueWindow` — the four deadline tiles of SCR-ACT-001, in the order they are drawn: what is
 * late, what is due this week, what can wait, what has no date (D-2561).
 */
const TASK_DUE_WINDOW_ORDER: Record<TaskDueWindow, true> = {
  OVERDUE: true,
  DUE_SOON: true,
  LATER: true,
  UNDATED: true,
};

export const TASK_DUE_WINDOWS = Object.keys(TASK_DUE_WINDOW_ORDER) as [
  TaskDueWindow,
  ...TaskDueWindow[],
];

/** The two states a task is still on the list in — `TaskItemRules.OpenStatuses`, mirrored. */
export const OPEN_TASK_STATUSES: readonly TaskItemStatus[] = ['OPEN', 'IN_PROGRESS'];

/** A registry label: the Romanian sentence for one `varchar` value, or nothing. */
export type RegistryLabel = (t: TFunction) => string;

/**
 * `TaskKinds` (Prop360.Domain/Notifications/TaskKinds.cs) — 03 §15.4's thirteen, in its order,
 * then the overdue repair ticket the Product Owner added after it (D-2474).
 *
 * All thirteen are labelled even though only six have a V1 generator (ALR-003 registers
 * `RentArrear`, `ContractRenewal`, `RevisionSchedule`, `DocumentRenew`, `InsuranceRenew` and
 * `NegativeProperty`). The other seven are the fiscal and money kinds M4 and later items write,
 * and a row arriving from one of them must read as Romanian on the day it arrives, not as a
 * screaming-snake constant.
 */
export const TASK_KIND_LABELS: Readonly<Record<string, RegistryLabel>> = {
  RENT_ARREAR: (t) => t('actionCenter.kinds.rentArrear'),
  C168_FILE: (t) => t('actionCenter.kinds.c168File'),
  FISCAL_PREPARE: (t) => t('actionCenter.kinds.fiscalPrepare'),
  FISCAL_CONFIRM_DATA: (t) => t('actionCenter.kinds.fiscalConfirmData'),
  DECLARATION_CONFIRM: (t) => t('actionCenter.kinds.declarationConfirm'),
  REVISION_SCHEDULE: (t) => t('actionCenter.kinds.revisionSchedule'),
  DOCUMENT_RENEW: (t) => t('actionCenter.kinds.documentRenew'),
  INSURANCE_RENEW: (t) => t('actionCenter.kinds.insuranceRenew'),
  CONTRACT_RENEWAL: (t) => t('actionCenter.kinds.contractRenewal'),
  NEGATIVE_PROPERTY: (t) => t('actionCenter.kinds.negativeProperty'),
  INDEXATION_APPLY: (t) => t('actionCenter.kinds.indexationApply'),
  DEPOSIT_RETURN: (t) => t('actionCenter.kinds.depositReturn'),
  RECONCILE_REVIEW: (t) => t('actionCenter.kinds.reconcileReview'),
  REPAIR_OVERDUE: (t) => t('actionCenter.kinds.repairOverdue'),
};

/** The kinds the filter offers, in 03 §15.4's order — `Object.keys` on a literal is insertion order. */
export const TASK_KINDS = Object.keys(TASK_KIND_LABELS);

/** The fiscal module's kinds — the server's `FiscalFamilies.Kinds`. */
const FISCAL_TASK_KINDS: readonly string[] = [
  'C168_FILE',
  'FISCAL_PREPARE',
  'FISCAL_CONFIRM_DATA',
  'DECLARATION_CONFIRM',
];

/**
 * Whether a task kind is withdrawn from view: a fiscal one while the fiscal module is (D-2780).
 * The server lists none of its rows then, so the filter neither offers it nor keeps it from an
 * old link.
 */
export function isWithdrawnTaskKind(kind: string | undefined): boolean {
  return kind !== undefined && !fiscalVisible() && FISCAL_TASK_KINDS.includes(kind);
}

/** {@link TASK_KINDS} less the withdrawn ones — the filter's fallback before the counts arrive. */
export function offeredTaskKinds(): string[] {
  return TASK_KINDS.filter((kind) => !isWithdrawnTaskKind(kind));
}

/**
 * The verb on each row's main button — what pressing it lets the landlord DO on the surface the
 * task's deep link opens (D-2563). The title says what is wrong; the button says what to do
 * about it, so it names the action taken there and not the kind: „Înregistrează încasarea" on
 * the rent board's late view, „Programează revizia" on the equipment page.
 *
 * Keyed exactly like `TASK_KIND_LABELS`, and held to the same registry by
 * `__tests__/enum-parity.test.ts`; a kind this build does not know falls back to
 * `actionCenter.cta.fallback` („Deschide") through `taskActionLabel`.
 */
export const TASK_KIND_ACTIONS: Readonly<Record<string, RegistryLabel>> = {
  RENT_ARREAR: (t) => t('actionCenter.cta.rentArrear'),
  C168_FILE: (t) => t('actionCenter.cta.c168File'),
  FISCAL_PREPARE: (t) => t('actionCenter.cta.fiscalPrepare'),
  FISCAL_CONFIRM_DATA: (t) => t('actionCenter.cta.fiscalConfirmData'),
  DECLARATION_CONFIRM: (t) => t('actionCenter.cta.declarationConfirm'),
  REVISION_SCHEDULE: (t) => t('actionCenter.cta.revisionSchedule'),
  DOCUMENT_RENEW: (t) => t('actionCenter.cta.documentRenew'),
  INSURANCE_RENEW: (t) => t('actionCenter.cta.insuranceRenew'),
  CONTRACT_RENEWAL: (t) => t('actionCenter.cta.contractRenewal'),
  NEGATIVE_PROPERTY: (t) => t('actionCenter.cta.negativeProperty'),
  INDEXATION_APPLY: (t) => t('actionCenter.cta.indexationApply'),
  DEPOSIT_RETURN: (t) => t('actionCenter.cta.depositReturn'),
  RECONCILE_REVIEW: (t) => t('actionCenter.cta.reconcileReview'),
  REPAIR_OVERDUE: (t) => t('actionCenter.cta.repairOverdue'),
};

/** The main button's verb for a kind, or „Deschide" for one this build has never heard of. */
export function taskActionLabel(kind: string, t: TFunction): string {
  const label = TASK_KIND_ACTIONS[kind];
  return label === undefined ? t('actionCenter.cta.fallback') : label(t);
}

/**
 * The icon at the head of each row: the thing the task is about, not its severity — the
 * severity is the tint (D-2563). A kind this build does not know gets the Action Center's own.
 */
export const TASK_KIND_ICONS: Readonly<Record<string, IconName>> = {
  RENT_ARREAR: 'banknotes',
  C168_FILE: 'document-text',
  FISCAL_PREPARE: 'scale',
  FISCAL_CONFIRM_DATA: 'identification',
  DECLARATION_CONFIRM: 'check-circle',
  REVISION_SCHEDULE: 'wrench',
  DOCUMENT_RENEW: 'folder',
  INSURANCE_RENEW: 'shield-check',
  CONTRACT_RENEWAL: 'calendar',
  NEGATIVE_PROPERTY: 'chart-bar',
  INDEXATION_APPLY: 'arrow-path',
  DEPOSIT_RETURN: 'credit-card',
  RECONCILE_REVIEW: 'building-library',
  REPAIR_OVERDUE: 'exclamation-triangle',
};

/** The icon for a kind, with the Action Center's own for one this build does not know. */
export function taskKindIcon(kind: string): IconName {
  return TASK_KIND_ICONS[kind] ?? 'clipboard-check';
}

/**
 * `NotificationRuleCatalog` — 03 §15.2's eight families plus the four later items appended
 * (`METER_INDEX_REQUEST` from MAINT-004, `PERSONAL_RENT_DUE` from OAT-002, `PORTAL_LINK` from
 * PORTAL-003 — the settings carrier of D-1051, labelled here since D-278 — `FISCAL_DOSSIER`
 * from REPORT-002, raised by the dossier job rather than by the nightly evaluation, and
 * `REPAIR_OVERDUE`, the switch of the Action Center's overdue repair ticket, D-2474).
 *
 * The screen renders whatever `GET /api/v1/notification-rules` returns, in the server's own
 * order, and looks the label up here; an organization created before a family existed simply
 * has no row and no line on the screen.
 */
export const RULE_TYPE_LABELS: Readonly<Record<string, RegistryLabel>> = {
  FINANCIAL_NEGATIVE: (t) => t('settings.notificationRules.families.financialNegative'),
  RENT_OVERDUE: (t) => t('settings.notificationRules.families.rentOverdue'),
  CONTRACT_EXPIRY: (t) => t('settings.notificationRules.families.contractExpiry'),
  REVISION_DUE: (t) => t('settings.notificationRules.families.revisionDue'),
  DOCUMENT_EXPIRY: (t) => t('settings.notificationRules.families.documentExpiry'),
  INSURANCE_EXPIRY: (t) => t('settings.notificationRules.families.insuranceExpiry'),
  FISCAL_OBLIGATION: (t) => t('settings.notificationRules.families.fiscalObligation'),
  INFO_DIGEST: (t) => t('settings.notificationRules.families.infoDigest'),
  METER_INDEX_REQUEST: (t) => t('settings.notificationRules.families.meterIndexRequest'),
  PERSONAL_RENT_DUE: (t) => t('settings.notificationRules.families.personalRentDue'),
  PORTAL_LINK: (t) => t('settings.notificationRules.families.portalLink'),
  FISCAL_DOSSIER: (t) => t('settings.notificationRules.families.fiscalDossier'),
  REPAIR_OVERDUE: (t) => t('settings.notificationRules.families.repairOverdue'),
  // Lane F — missing producers: four families raised by events, not by the nightly pass (D-3020).
  OWNERSHIP_TRANSFER: (t) => t('settings.notificationRules.families.ownershipTransfer'),
  GDPR_EXPORT: (t) => t('settings.notificationRules.families.gdprExport'),
  INCIDENT_REPORTED: (t) => t('settings.notificationRules.families.incidentReported'),
  PAYMENT_DECLARED: (t) => t('settings.notificationRules.families.paymentDeclared'),
  // Lane D — renter mail: the rent reminder addressed to each renter (D-3040).
  TENANT_RENT_DUE: (t) => t('settings.notificationRules.families.tenantRentDue'),
};

/** The fiscal module's notification families — the server's `FiscalFamilies.RuleTypes`. */
const FISCAL_RULE_TYPES: readonly string[] = ['FISCAL_OBLIGATION', 'FISCAL_DOSSIER'];

/**
 * Whether a family is withdrawn from view: a fiscal one while the fiscal module is (D-2780).
 * The rules screen does not list it then, and the server neither shows nor mails its rows.
 */
export function isWithdrawnRuleType(ruleType: string): boolean {
  return !fiscalVisible() && FISCAL_RULE_TYPES.includes(ruleType);
}

/**
 * A registry value's Romanian name, or the raw value when this build has never heard of it.
 *
 * Returning the value itself is the honest fallback: a `varchar` registry can grow on the
 * server without a client release, and „RENT_ARREAR" beside a title tells a landlord more than
 * an empty cell and tells support exactly what to look for.
 */
export function registryLabel(
  table: Readonly<Record<string, RegistryLabel>>,
  value: string,
  t: TFunction,
): string {
  const label = table[value];
  return label === undefined ? value : label(t);
}

/** V1 delivers on two of `NotificationChannel`'s five; the rest are schema-ready (02 §12). */
export const DELIVERED_CHANNELS = ['IN_APP', 'EMAIL'] as const;

export type DeliveredChannel = (typeof DELIVERED_CHANNELS)[number];
